import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase-config';

export default function InlineAuthModal({ onSuccess }) {
  const [mode, setMode]       = useState('choose'); // choose | email-signup | email-signin | verify-wait
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [shaking, setShaking] = useState(false);
  const shakeTimer = useRef(null);
  const pollRef = useRef(null);

  // Clean up polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  function handleBackdropClick() {
    if (shaking) return;
    setShaking(true);
    shakeTimer.current = setTimeout(() => setShaking(false), 420);
  }

  // ── Google sign-in ────────────────────────────────────────
  async function handleGoogle() {
    setError('');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const { user } = await signInWithPopup(auth, provider);
      const displayName = user.displayName?.trim() || user.email?.split('@')[0] || 'Learner';

      // Force token refresh so Firestore rules see the new auth immediately
      await user.getIdToken(true);

      // Mark session active so AuthContext doesn't immediately sign the user out
      sessionStorage.setItem('om_session_active', '1');

      // Ensure Firestore profile exists
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!snap.exists()) {
          await setDoc(doc(db, 'users', user.uid), {
            displayName,
            email: user.email?.toLowerCase() || '',
            tier: null,
            createdAt: serverTimestamp(),
          });
        }
      } catch {
        // Profile write failed — non-fatal, user is still authenticated
      }
      onSuccess(displayName);
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // User closed popup — not an error
      } else {
        console.error('[Google sign-in error]', err.code, err.message);
        setError(`Google sign-in failed (${err.code || 'unknown'}). Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Email signup ──────────────────────────────────────────
  async function handleSignup(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const displayName = name.trim() || email.split('@')[0];
      await setDoc(doc(db, 'users', user.uid), {
        displayName,
        email: email.trim().toLowerCase(),
        tier: null,
        createdAt: serverTimestamp(),
      });
      await sendEmailVerification(user);
      // Don't sign out — start polling for verification
      setMode('verify-wait');
      startVerificationPoll(user, displayName);
    } catch (err) {
      const msgs = {
        'auth/email-already-in-use': 'That email is taken. Try signing in instead.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/weak-password': 'Password must be at least 8 characters.',
      };
      setError(msgs[err.code] || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Email signin ──────────────────────────────────────────
  async function handleSignin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { user } = await signInWithEmailAndPassword(auth, email.trim(), password);
      if (!user.emailVerified) {
        await sendEmailVerification(user).catch(() => {});
        setMode('verify-wait');
        startVerificationPoll(user, null);
        return;
      }
      sessionStorage.setItem('om_session_active', '1');
      const snap = await getDoc(doc(db, 'users', user.uid));
      const displayName = snap.data()?.displayName || snap.data()?.name || email.split('@')[0];
      onSuccess(displayName);
    } catch (err) {
      const msgs = {
        'auth/user-not-found':    'No account found with that email.',
        'auth/wrong-password':    'Incorrect password.',
        'auth/invalid-credential': 'Invalid email or password.',
        'auth/too-many-requests': 'Too many attempts. Try again in a few minutes.',
      };
      setError(msgs[err.code] || 'Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Poll for email verification ───────────────────────────
  const startVerificationPoll = useCallback((user, displayNameOverride) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        await user.reload();
        if (user.emailVerified) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          sessionStorage.setItem('om_session_active', '1');
          const snap = await getDoc(doc(db, 'users', user.uid));
          const displayName = displayNameOverride || snap.data()?.displayName || user.email?.split('@')[0];
          onSuccess(displayName);
        }
      } catch { /* keep polling */ }
    }, 3000);
  }, [onSuccess]);

  const inputStyle = {
    width: '100%',
    padding: '11px 14px',
    backgroundColor: '#0d1525',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    color: '#e8eeff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '11px',
    color: '#6b7a99',
    marginBottom: '6px',
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0,
        background: 'repeating-linear-gradient(-24deg, rgba(152,236,255,0.04) 0, rgba(152,236,255,0.04) 1px, transparent 1px, transparent 84px), radial-gradient(120% 80% at 18% 16%, rgba(56,189,248,0.12), transparent 52%), radial-gradient(120% 90% at 76% 74%, rgba(167,139,250,0.1), transparent 58%), rgba(5, 8, 18, 0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(6px)',
      }}
    >
      <style>{`
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          15%      { transform: translateX(-8px); }
          30%      { transform: translateX(7px); }
          45%      { transform: translateX(-6px); }
          60%      { transform: translateX(5px); }
          75%      { transform: translateX(-3px); }
          90%      { transform: translateX(2px); }
        }
        @keyframes pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(0,180,255,0.3); }
          70% { box-shadow: 0 0 0 12px rgba(0,180,255,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,180,255,0); }
        }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '400px',
          background: 'linear-gradient(165deg, rgba(8,18,44,0.95), rgba(7,12,29,0.85))',
          border: '1px solid rgba(153,179,214,0.2)',
          borderRadius: '16px',
          padding: '36px 32px',
          margin: '0 16px',
          boxShadow: '0 34px 78px rgba(1,6,20,0.58), 0 12px 24px rgba(1,6,20,0.42)',
          animation: shaking ? 'shake 0.42s ease' : 'none',
        }}
      >
        {/* Brand */}
        <div style={{ fontSize: '10px', letterSpacing: '0.25em', color: '#00b4ff', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase' }}>
          The Operator Method
        </div>

        {/* ── Choose mode ──────────────────────────────────── */}
        {mode === 'choose' && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#e8eeff', margin: '0 0 6px 0' }}>
              Save your results
            </h2>
            <p style={{ fontSize: '13px', color: '#5a7a9a', margin: '0 0 24px 0', lineHeight: 1.5 }}>
              Create a free account to keep your diagnostic and see your personalized plan.
            </p>

            {error && <div style={errorStyle}>{error}</div>}

            {/* Google button */}
            <button
              onClick={handleGoogle}
              disabled={loading}
              style={{
                width: '100%',
                padding: '13px',
                backgroundColor: '#fff',
                color: '#333',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.002 24.002 0 000 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              {loading ? 'Connecting…' : 'Continue with Google'}
            </button>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#1e2d45' }} />
              <span style={{ fontSize: '11px', color: '#4a5e7e', textTransform: 'uppercase', letterSpacing: '0.1em' }}>or</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#1e2d45' }} />
            </div>

            {/* Email options */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setMode('email-signup'); setError(''); }}
                style={secondaryBtnStyle}
              >
                Sign up with email
              </button>
              <button
                onClick={() => { setMode('email-signin'); setError(''); }}
                style={secondaryBtnStyle}
              >
                Sign in
              </button>
            </div>
          </>
        )}

        {/* ── Email signup ─────────────────────────────────── */}
        {mode === 'email-signup' && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#e8eeff', margin: '0 0 20px 0' }}>
              Create your account
            </h2>

            {error && <div style={errorStyle}>{error}</div>}

            <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={labelStyle}>Name <span style={{ opacity: 0.5, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="First name or nickname" style={inputStyle} autoFocus />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Password <span style={{ opacity: 0.5, textTransform: 'none', letterSpacing: 0 }}>(min. 8 characters)</span></label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" style={inputStyle} />
              </div>
              <button type="submit" disabled={loading} style={primaryBtnStyle(loading)}>
                {loading ? 'Creating account…' : 'Create account →'}
              </button>
            </form>

            <button onClick={() => { setMode('choose'); setError(''); }} style={backLink}>← Back</button>
          </>
        )}

        {/* ── Email signin ──────────────────────────────────── */}
        {mode === 'email-signin' && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#e8eeff', margin: '0 0 20px 0' }}>
              Welcome back
            </h2>

            {error && <div style={errorStyle}>{error}</div>}

            <form onSubmit={handleSignin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" style={inputStyle} autoFocus />
              </div>
              <div>
                <label style={labelStyle}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" style={inputStyle} />
              </div>
              <button type="submit" disabled={loading} style={primaryBtnStyle(loading)}>
                {loading ? 'Signing in…' : 'Sign in →'}
              </button>
            </form>

            <button onClick={() => { setMode('choose'); setError(''); }} style={backLink}>← Back</button>
          </>
        )}

        {/* ── Verification wait ─────────────────────────────── */}
        {mode === 'verify-wait' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%',
              border: '3px solid rgba(0,180,255,0.4)',
              margin: '0 auto 20px',
              animation: 'pulse-ring 1.5s ease-in-out infinite',
            }} />
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#e8eeff', margin: '0 0 10px 0' }}>
              Check your email
            </h2>
            <p style={{ fontSize: '14px', color: '#7a9fc0', lineHeight: 1.6, margin: '0 0 8px 0' }}>
              We sent a verification link to <strong style={{ color: '#b0d0f0' }}>{email}</strong>.
            </p>
            <p style={{ fontSize: '13px', color: '#4a6a8a', lineHeight: 1.5, margin: 0 }}>
              Click the link in your inbox — this page will continue automatically once verified.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shared styles ───────────────────────────────────────────
const errorStyle = {
  padding: '10px 14px', backgroundColor: '#1a0f0f',
  border: '1px solid #4a1515', borderRadius: '8px',
  color: '#f87171', fontSize: '13px', marginBottom: '16px',
};

const secondaryBtnStyle = {
  flex: 1,
  padding: '11px',
  backgroundColor: 'transparent',
  color: '#7a9fc0',
  border: '1px solid rgba(0,180,255,0.18)',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
};

const primaryBtnStyle = (loading) => ({
  marginTop: '4px',
  padding: '12px',
  backgroundColor: loading ? '#1a2540' : '#0066cc',
  color: loading ? '#4a5e7e' : '#fff',
  border: 'none', borderRadius: '8px',
  fontSize: '14px', fontWeight: 600,
  cursor: loading ? 'not-allowed' : 'pointer',
});

const backLink = {
  background: 'none', border: 'none',
  color: '#4a6a8a', fontSize: '13px',
  cursor: 'pointer', marginTop: '16px',
  padding: 0,
};
